
// Forward type-only import to avoid circular dependency at runtime
import type { ReactiveEffectLike } from './lifecycle'
import { getRequestContext } from './request-context'

export interface ComponentInstance {
    hooks: unknown[]
    hookIndex: number
    // Phase 5 hidden-class stabilization: all fields are non-optional with
    // `null` as the unallocated sentinel. Pre-initializing every slot in the
    // constructor literal locks the V8 hidden class so all ComponentInstance
    // objects share one IC entry, instead of transitioning through 4-7 hidden
    // classes as fields were lazy-added (effects, globalKeys, hookSignatures).
    hookSignatures: string[] | null
    prevHookSignatures: string[] | null
    componentName: string | null
    /** Effects registered by unsafeEffect() inside this component's render. Stopped on unmount. */
    effects: ReactiveEffectLike[] | null
    /** Global registry keys held by this component instance. Decremented on unmount. */
    globalKeys: string[] | null
}

// Phase 6A: module-scope currentComponent fast-path (browser only).
// In SSR, concurrent requests need AsyncLocalStorage isolation — we must
// NOT use a shared module-scope slot there. Detection is done once at
// module init so the resolver branch is never hit at call time.
//
// Window-shimmed SSR environments (Next.js Edge, Deno --unstable-window,
// some test runners) define `window` but not `document`. Requiring BOTH
// globals here keeps us strictly browser-only so SSR cross-request
// contamination is impossible.
const _isBrowser = typeof window !== 'undefined' && typeof document !== 'undefined'

/** Browser-only fast-path: direct module-scope slot, zero function call. */
let _browserCurrentComponent: ComponentInstance | null = null

export function getComponent(): ComponentInstance | null {
    if (_isBrowser) {
        return _browserCurrentComponent
    }
    return getRequestContext().currentComponent
}

export function runWithComponent<T>(component: ComponentInstance, fn: () => T): T {
    let prev: ComponentInstance | null
    if (_isBrowser) {
        prev = _browserCurrentComponent
        _browserCurrentComponent = component
    } else {
        const ctx = getRequestContext()
        prev = ctx.currentComponent
        ctx.currentComponent = component
    }
    component.hookIndex = 0
    if (import.meta.env.DEV) {
        component.prevHookSignatures = component.hookSignatures
        component.hookSignatures = []
    }
    try {
        return fn()
    } finally {
        if (import.meta.env.DEV) {
            checkHookOrder(component)
        }
        if (_isBrowser) {
            _browserCurrentComponent = prev
        } else {
            getRequestContext().currentComponent = prev
        }
    }
}

export function hook<T>(factory: () => T, label?: string): T {
    const currentComponent = _isBrowser
        ? _browserCurrentComponent
        : getRequestContext().currentComponent
    if (!currentComponent) {
        // Outside component: just run factory
        return factory()
    }

    const instance = currentComponent
    const { hooks, hookIndex } = instance

    if (import.meta.env.DEV) {
        instance.hookSignatures!.push(label ?? factory.name ?? 'use')
    }

    if (hookIndex < hooks.length) {
        // Return existing hook
        instance.hookIndex++
        return hooks[hookIndex] as T
    }

    // Create new hook
    const value = factory()
    hooks.push(value)
    instance.hookIndex++

    return value
}

function checkHookOrder(c: ComponentInstance) {
    if (!c.prevHookSignatures || !c.hookSignatures) return
    const prev = c.prevHookSignatures
    const curr = c.hookSignatures
    const name = c.componentName ?? 'Component'
    if (prev.length !== curr.length) {
        console.warn(
            `[flexium] Hook order changed in ${name}: previous render had ${prev.length} hooks, this render has ${curr.length}. ` +
            `Hooks must be called in the same order every render.`
        )
        return
    }
    for (let i = 0; i < curr.length; i++) {
        if (prev[i] !== curr[i]) {
            console.warn(
                `[flexium] Hook order mismatch at index ${i} in ${name}: previous "${prev[i]}", this render "${curr[i]}".`
            )
            return
        }
    }
}

/**
 * Register a reactive effect with the currently rendering component instance so it
 * will be stopped when the component is unmounted.
 */
export function registerEffectWithComponent(c: ComponentInstance, e: ReactiveEffectLike) {
    if (!c.effects) c.effects = []
    c.effects.push(e)
}

/**
 * Stop and clear all effects that were registered with the given component instance.
 * Called during component unmount.
 */
export function stopComponentEffects(c: ComponentInstance) {
    if (c.effects) {
        for (const e of c.effects) e.stop()
        c.effects.length = 0
    }
}

// ---------------------------------------------------------------------------
// Global registry release handler — late-bound to avoid circular imports.
// use.ts calls setGlobalKeyReleaseHandler() at module-init time to register
// the actual implementation; hook.ts never imports from use.ts.
// ---------------------------------------------------------------------------

let releaseKeyHandler: ((key: string) => void) | null = null

/**
 * Called by use.ts at module initialization to register the globalRegistry
 * release function without creating a circular import.
 */
export function setGlobalKeyReleaseHandler(fn: (key: string) => void): void {
    releaseKeyHandler = fn
}

/**
 * Decrement the refCount for every global registry key held by this component
 * and evict any entry whose refCount reaches zero.
 * Called during component unmount, alongside stopComponentEffects().
 */
export function releaseComponentGlobals(c: ComponentInstance): void {
    if (c.globalKeys && releaseKeyHandler) {
        for (const key of c.globalKeys) releaseKeyHandler(key)
        c.globalKeys.length = 0
    }
}
